import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";

// Absolute URL so the request always reaches the API Worker (api.seavault.uk),
// not Cloudflare Pages (seavault.uk) which has no API routes.
const API_BASE = (import.meta as any).env?.VITE_API_URL ?? "";

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.07)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: "8px",
  color: "white",
  fontSize: "14px",
  padding: "10px 14px",
  outline: "none",
};

export default function RequestAccessPage() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    fleetSize: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          company: form.company,
          email: form.email,
          fleetSize: form.fleetSize,
          message: form.message || undefined,
        }),
      });
      const data = (await res.json()) as { success?: boolean; error?: string };
      if (!res.ok) {
        throw new Error(data.error ?? "Something went wrong. Please try again.");
      }
      // Only mark as sent on success — form values are intentionally preserved
      // in state on error so the user doesn't have to retype anything.
      setSent(true);
    } catch (err) {
      // Do NOT call setForm here — keep all field values intact for the user.
      setFormError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col font-sans"
      style={{ backgroundColor: "#0c2340" }}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-5 max-w-2xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-300" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="12" y1="2" x2="12" y2="4" />
            <line x1="12" y1="20" x2="12" y2="22" />
            <line x1="2" y1="12" x2="4" y2="12" />
            <line x1="20" y1="12" x2="22" y2="12" />
          </svg>
          <span className="text-white font-bold text-lg tracking-tight">SeaVault</span>
        </Link>
        <Link
          to="/login"
          className="text-sm font-medium text-white border border-white/30 rounded-lg px-4 py-2 hover:bg-white/10 transition-colors"
        >
          Login
        </Link>
      </header>

      {/* Form */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-10">
            <h1 className="text-2xl font-bold text-white mb-3">Get started with SeaVault</h1>
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>
              Tell us about your fleet and we'll be in touch within 24 hours.
            </p>
          </div>

          {sent ? (
            <div
              className="rounded-2xl p-10 text-center"
              style={{ backgroundColor: "#133262", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: "rgba(74,222,128,0.15)" }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p className="text-white font-semibold text-lg">
                Thanks {form.name} — we'll be in touch within 24 hours.
              </p>
              <Link
                to="/"
                className="inline-block mt-6 text-sm"
                style={{ color: "rgba(255,255,255,0.5)" }}
              >
                ← Back to home
              </Link>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl p-8 space-y-5"
              style={{ backgroundColor: "#133262", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.8)" }}>
                  Full Name <span style={{ color: "#f87171" }}>*</span>
                </label>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Your full name"
                  style={inputStyle}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#1d5199")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)")}
                />
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.8)" }}>
                  Company <span style={{ color: "#f87171" }}>*</span>
                </label>
                <input
                  required
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  placeholder="Shipping company name"
                  style={inputStyle}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#1d5199")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)")}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.8)" }}>
                  Email <span style={{ color: "#f87171" }}>*</span>
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="you@company.com"
                  style={inputStyle}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#1d5199")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)")}
                />
              </div>

              {/* Fleet Size */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.8)" }}>
                  Fleet Size <span style={{ color: "#f87171" }}>*</span>
                </label>
                <select
                  required
                  value={form.fleetSize}
                  onChange={(e) => setForm((f) => ({ ...f, fleetSize: e.target.value }))}
                  style={{
                    ...inputStyle,
                    color: form.fleetSize ? "white" : "rgba(255,255,255,0.4)",
                    appearance: "none",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.5)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 14px center",
                    paddingRight: "36px",
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#1d5199")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)")}
                >
                  <option value="" disabled style={{ backgroundColor: "#133262" }}>Select fleet size</option>
                  <option value="1–5 vessels" style={{ backgroundColor: "#133262" }}>1–5 vessels</option>
                  <option value="6–15 vessels" style={{ backgroundColor: "#133262" }}>6–15 vessels</option>
                  <option value="16–50 vessels" style={{ backgroundColor: "#133262" }}>16–50 vessels</option>
                  <option value="50+ vessels" style={{ backgroundColor: "#133262" }}>50+ vessels</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.8)" }}>
                  Message{" "}
                  <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  placeholder="Anything you'd like us to know"
                  rows={3}
                  style={{ ...inputStyle, resize: "none" }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#1d5199")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)")}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: "100%",
                  backgroundColor: submitting ? "rgba(29,81,153,0.6)" : "#1d5199",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  padding: "12px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: submitting ? "not-allowed" : "pointer",
                  transition: "background-color 0.15s",
                }}
              >
                {submitting ? "Sending…" : "Request Access"}
              </button>

              {/* Inline error */}
              {formError && (
                <div
                  className="rounded-lg px-4 py-3 text-sm"
                  style={{
                    backgroundColor: "rgba(239,68,68,0.15)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    color: "#fca5a5",
                  }}
                >
                  {formError}
                </div>
              )}
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
